import type { InputStatus } from 'antd/lib/_util/statusUtils';
import type { BaseType } from 'antd/lib/typography/Base';
import { type JSX, useCallback, useEffect, useMemo } from 'react';

import { ValidationError } from 'src/components/base/fields/common/ValidationError/ValidationError';
import type {
  DimensionsFieldValue,
  DimensionsValue,
} from 'src/components/base/fields/SpecificationField/SpecificationDimensionsField/SpecificationDimensionsField.types';
import { SpecificationDimensionsMultiselect } from 'src/components/base/fields/SpecificationField/SpecificationDimensionsField/SpecificationDimensionsMultiselect';
import { withField, withFieldError } from 'src/helpers';
import type { IWithFieldErrorProps } from 'src/helpers/withFieldError';
import type { IFieldProps } from 'src/types';
import type { IProductSpecificationFieldReference } from 'src/types/reference/IProductSpecificationReference';

type SpecificationDimensionsFieldProps = IFieldProps &
  Omit<IWithFieldErrorProps, 'isShowErrorMessage'> & {
    specificationField: IProductSpecificationFieldReference;

    containerClassName?: string;
    className?: string;
    errorType: BaseType;
    status?: InputStatus;
    disabled?: boolean;
  };

export const SpecificationDimensionsMultiselectField = withField(
  withFieldError(
    ({
      field: formikField,
      form: { setFieldValue, setFieldTouched },
      touchOnMount,

      specificationField,
      containerClassName,
      className,
      errorType,
      status,
      disabled,
    }: Readonly<SpecificationDimensionsFieldProps>): JSX.Element => {
      // biome-ignore lint/correctness/useExhaustiveDependencies: false
      const defaultValue: DimensionsValue = useMemo(() => {
        const fieldValue = formikField.value as unknown as DimensionsFieldValue;

        if (fieldValue === undefined) {
          return specificationField.multiselect ? [] : null;
        }

        if (fieldValue === null && specificationField.multiselect) {
          return [];
        }

        return Array.isArray(fieldValue) ? fieldValue.filter(Boolean) : fieldValue;
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, []);

      const handleChange = useCallback(
        async (selectedValue: DimensionsValue) => {
          await setFieldValue(formikField.name, selectedValue);
          await setFieldTouched(formikField.name, true);
        },
        [setFieldValue, formikField.name, setFieldTouched]
      );

      // biome-ignore lint/correctness/useExhaustiveDependencies: false
      useEffect(() => {
        if (touchOnMount) {
          void setFieldTouched(formikField.name, true);
        }
      }, []);

      return (
        <div className="flex column nowrap align-items-stretch gap-tiny">
          <SpecificationDimensionsMultiselect
            defaultValue={defaultValue}
            onChange={handleChange}
            field={specificationField}
            containerClassName={containerClassName}
            className={className}
            status={status}
            disabled={disabled}
          />
          <div className="relative">
            <ValidationError name={formikField.name} errorType={errorType} />
          </div>
        </div>
      );
    }
  )
);
